// src/packages/care/prediction/bayesianCycleModel.ts
//
// Adaptive Bayesian cycle model — the personalized Phase 1 predictor (issue #1908).
//
// This module owns the ESTIMATION: a client-side hierarchical conjugate estimate of the
// patient's cycle-length distribution that updates as logged cycles accumulate.
//
//   • Cycle length μ  — Normal–Normal conjugate update. Prior mean = age-adjusted population
//     mean (or the patient's declared average); prior variance = between-woman variance. Each
//     observed cycle updates the posterior; with sparse history the estimate stays near the
//     population prior (shrinkage) and personalizes as evidence arrives.
//   • Within-woman variance σ² — inverse-gamma-style shrinkage blend of the population
//     within-woman variance and the observed sample variance, so irregular cyclers get wider
//     (more honest) intervals automatically.
//
// The estimate is handed to the shared projection (cycleProjection.ts) which turns it into
// ovulation + the ASRM fertile window — that geometry is identical across models. All clinical
// constants live in knowledgeBase.ts.

import {
  CYCLE_LENGTH,
  REGULARITY,
  DATA_SUFFICIENCY,
  KNOWLEDGE_BASE_VERSION,
} from "./knowledgeBase";
import { getPeriodStartDates } from "./evidenceMapper";
import { getObservedCycleLengths, mean, clamp } from "./cycleStats";
import { projectFertileWindow } from "./cycleProjection";
import type { PatientEvidence } from "./evidence.types";
import type {
  FertileWindowPrediction,
  PredictionConfidence,
  PredictionStatus,
  PredictionWarning,
  LocalDate,
} from "./fertileWindow.types";

export const MODEL_ID = "bayesian-cycle";
export const MODEL_VERSION = "1.0.0";

/**
 * Prior strength (in pseudo-cycles) for the within-woman variance shrinkage. Higher = the
 * population within-woman SD dominates until more cycles are observed.
 */
const VARIANCE_PRIOR_STRENGTH = 4;

function ageAdjustedMean(ageYears: number | null): number {
  if (ageYears == null) {
    return CYCLE_LENGTH.populationMeanDays;
  }
  const adjusted =
    CYCLE_LENGTH.populationMeanDays +
    CYCLE_LENGTH.ageSlopeDaysPerYear * (ageYears - CYCLE_LENGTH.ageAnchorYears);
  return clamp(
    adjusted,
    CYCLE_LENGTH.normalRangeDays.min,
    CYCLE_LENGTH.normalRangeDays.max,
  );
}

/** Prior mean for cycle length: the patient's declared average if given, else age-adjusted population. */
function priorMean(evidence: PatientEvidence): number {
  const declared = evidence.declaredCycleLengthDays;
  if (declared && declared.min != null && declared.max != null) {
    return (declared.min + declared.max) / 2;
  }
  return ageAdjustedMean(evidence.ageYears);
}

function emptyPrediction(
  status: PredictionStatus,
  confidence: PredictionConfidence,
  warnings: PredictionWarning[],
): FertileWindowPrediction {
  return {
    status,
    nextPeriod: null,
    ovulation: null,
    fertileDays: [],
    confidence,
    modelId: MODEL_ID,
    modelVersion: `${MODEL_VERSION}+kb.${KNOWLEDGE_BASE_VERSION}`,
    evidenceUsed: [
      { type: "period_start", observationCount: 0, status: "ignored" },
    ],
    warnings,
  };
}

/**
 * Produce a fertile-window prediction from the patient's evidence, relative to `today`.
 * Returns a complete FertileWindowPrediction; the orchestrator may further downgrade it.
 */
export function predictFertileWindow(
  evidence: PatientEvidence,
  today: LocalDate,
): FertileWindowPrediction {
  const starts = getPeriodStartDates(evidence);

  if (starts.length === 0) {
    return emptyPrediction("insufficient_data", "low", []);
  }

  const cycleLengths = getObservedCycleLengths(starts);
  const rejectedGaps = starts.length - 1 - cycleLengths.length;
  const observedCycles = cycleLengths.length;

  const warnings: PredictionWarning[] = [];
  if (observedCycles < DATA_SUFFICIENCY.minCyclesForPersonalized) {
    warnings.push("limited_history");
  }

  // --- Estimate the cycle-length distribution (Normal–Normal posterior) ------------------
  const mu0 = priorMean(evidence);
  const priorVarMean = CYCLE_LENGTH.populationSdDays ** 2; // between-woman variance = prior on the mean
  const priorWithinVar = CYCLE_LENGTH.withinWomanSdDays ** 2;

  // Within-woman variance σ² — shrinkage blend of population prior and observed spread.
  let sampleMean = mu0;
  let withinVar = priorWithinVar;
  if (observedCycles >= 1) {
    sampleMean = mean(cycleLengths);
    const ss = cycleLengths.reduce((acc, x) => acc + (x - sampleMean) ** 2, 0);
    withinVar =
      (VARIANCE_PRIOR_STRENGTH * priorWithinVar + ss) /
      (VARIANCE_PRIOR_STRENGTH + observedCycles);
  }

  // Conjugate update of μ using the observed cycles.
  let postMean = mu0;
  let postVarMean = priorVarMean;
  if (observedCycles >= 1) {
    const priorPrecision = 1 / priorVarMean;
    const likePrecision = observedCycles / withinVar;
    postVarMean = 1 / (priorPrecision + likePrecision);
    postMean =
      postVarMean *
      (mu0 / priorVarMean + (observedCycles * sampleMean) / withinVar);
  }

  const personalized =
    observedCycles >= DATA_SUFFICIENCY.minCyclesForPersonalized;
  const lastStart = starts[starts.length - 1]!;

  const projection = projectFertileWindow(
    { cycleMeanDays: postMean, cycleMeanVar: postVarMean, withinVar },
    lastStart,
    today,
    personalized,
  );

  // --- Regularity, status, confidence ---------------------------------------------------
  let irregular = false;
  if (observedCycles >= 2) {
    const range = Math.max(...cycleLengths) - Math.min(...cycleLengths);
    irregular = range > REGULARITY.irregularRangeThresholdDays;
    if (irregular) {
      warnings.push("irregular_history");
    }
  }

  const status: PredictionStatus = personalized ? "available" : "partial";

  let confidence: PredictionConfidence;
  if (
    observedCycles >= DATA_SUFFICIENCY.minCyclesForFullConfidence &&
    !irregular
  ) {
    confidence = "high";
  } else if (
    observedCycles >= DATA_SUFFICIENCY.minCyclesForPersonalized &&
    !irregular
  ) {
    confidence = "medium";
  } else {
    confidence = "low";
  }

  const evidenceUsed: FertileWindowPrediction["evidenceUsed"] = [
    {
      type: "period_start",
      observationCount: starts.length,
      status: "applied",
      note: `${observedCycles} usable cycle${observedCycles === 1 ? "" : "s"} from ${starts.length} logged period start${starts.length === 1 ? "" : "s"}`,
    },
  ];
  if (rejectedGaps > 0) {
    evidenceUsed.push({
      type: "period_start",
      observationCount: rejectedGaps,
      status: "invalid",
      note: "gap outside plausible cycle range (likely a missed or duplicate log)",
    });
  }

  return {
    status,
    nextPeriod: projection.nextPeriod,
    ovulation: projection.ovulation,
    fertileDays: projection.fertileDays,
    confidence,
    modelId: MODEL_ID,
    modelVersion: `${MODEL_VERSION}+kb.${KNOWLEDGE_BASE_VERSION}`,
    evidenceUsed,
    warnings,
  };
}
